(() => {
  'use strict';
  if (customElements.get('beerclub-header')) return;

  let heightOwner = null;

  class BeerclubHeader extends HTMLElement {
    static get observedAttributes() { return ['data-sticky', 'data-sticky-mode']; }

    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.wrapper = this.closest('.bc-header-section');
      this.headerHidden = false;
      this.headerHeight = 0;
      this.keyboardNavigation = false;
      this.editorSectionSelected = false;
      this.editorBlockId = null;
      this.lastMetrics = '';
      this.wrapper?.classList.remove('bc-header-is-hidden');
      this.resetScrollTracking();
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.mobileMenu = this.querySelector('details[data-mobile-menu]');
      this.megaMenus = [...this.querySelectorAll('details[data-mega-menu]')];
      this.closeButtons = [...this.querySelectorAll('[data-menu-close]')];
      this.closeButtons.forEach(button => { button.hidden = false; });
      this.classList.add('is-enhanced');

      this.addEventListener('toggle', event => {
        const menu = event.target;
        if (menu === this.mobileMenu) {
          if (menu.open) {
            this.closeMegaMenus();
            if (this.desktop.matches) this.close(menu);
          }
        } else if (this.megaMenus.includes(menu) && menu.open) {
          this.closeMegaMenus(menu);
          this.close(this.mobileMenu);
          if (!this.desktop.matches) this.close(menu);
        }
        if (this.hasOpenMenu()) this.revealAndReset();
        this.queueMeasure();
      }, { signal, capture: true });

      this.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const closeButton = event.target.closest('[data-menu-close]');
        if (closeButton && this.contains(closeButton)) {
          event.preventDefault();
          this.close(this.mobileMenu, true);
          return;
        }
        const link = event.target.closest('a[href]');
        if (!link) return;
        // Let native links retain their destination, modifiers and focus behavior.
        if (this.mobileMenu?.contains(link)) this.close(this.mobileMenu);
        this.megaMenus.forEach(menu => { if (menu.contains(link)) this.close(menu); });
      }, { signal });

      document.addEventListener('click', event => {
        this.megaMenus.forEach(menu => { if (!menu.contains(event.target)) this.close(menu); });
        if (this.mobileMenu && !this.mobileMenu.contains(event.target)) this.close(this.mobileMenu);
      }, { signal });

      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || event.defaultPrevented) return;
        const current = this.megaMenus.find(menu => menu.open && menu.contains(event.target))
          || (this.mobileMenu?.open ? this.mobileMenu : null)
          || this.megaMenus.find(menu => menu.open);
        if (!current) return;
        event.preventDefault();
        this.keyboardNavigation = true;
        this.close(current, true);
      }, { signal });

      this.addEventListener('focusout', event => {
        const current = this.megaMenus.find(menu => menu.open && menu.contains(event.target))
          || (this.mobileMenu?.open && this.mobileMenu.contains(event.target) ? this.mobileMenu : null);
        if (!current) return;
        // Focus can briefly be on body between blur and focus. Internal moves
        // must not collapse the menu before its next link receives focus.
        if (event.relatedTarget) {
          if (!current.contains(event.relatedTarget)) this.close(current);
        } else {
          requestAnimationFrame(() => {
            if (!signal.aborted && this.isConnected && !current.contains(document.activeElement)) this.close(current);
          });
        }
      }, { signal });

      this.desktop.addEventListener('change', () => {
        // Release hidden disclosures when crossing between the two navigation layouts.
        this.close(this.mobileMenu);
        this.closeMegaMenus();
        this.revealAndReset();
        this.queueMeasure();
      }, { signal });

      document.addEventListener('shopify:block:select', event => {
        const blockId = event.detail?.blockId;
        if (!blockId) return;
        const ownsBlock = this.ownsEditorEvent(event)
          || [...this.querySelectorAll('[data-block-id]')].some(item => item.dataset.blockId === blockId);
        if (!ownsBlock) return;
        this.editorBlockId = blockId;
        this.revealAndReset();
        if (this.desktop.matches) {
          const menu = this.megaMenus.find(item => item.dataset.blockId === blockId);
          if (!menu) return;
          this.closeMegaMenus(menu);
          menu.open = true;
        } else if (this.mobileMenu) {
          const category = [...this.mobileMenu.querySelectorAll('details.bc-mobile-category[data-block-id]')]
            .find(item => item.dataset.blockId === blockId);
          if (!category) return;
          this.closeMegaMenus();
          this.mobileMenu.open = true;
          category.open = true;
        }
      }, { signal });

      document.addEventListener('shopify:block:deselect', event => {
        if (this.editorBlockId !== event.detail?.blockId) return;
        this.editorBlockId = null;
        this.resetScrollTracking();
      }, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (!this.ownsEditorEvent(event)) return;
        this.editorSectionSelected = true;
        this.revealAndReset();
      }, { signal });
      document.addEventListener('shopify:section:deselect', event => {
        if (!this.ownsEditorEvent(event)) return;
        this.editorSectionSelected = false;
        this.resetScrollTracking();
      }, { signal });

      document.addEventListener('keydown', event => {
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        this.keyboardNavigation = true;
        if (this.contains(document.activeElement)) this.revealAndReset();
      }, { signal, capture: true });
      document.addEventListener('pointerdown', () => {
        // A simple click can leave focus on a button; only keyboard focus pins the header.
        this.keyboardNavigation = false;
        this.resetScrollTracking();
      }, { signal, capture: true, passive: true });
      this.addEventListener('focusin', () => this.revealAndReset(), { signal });

      window.addEventListener('scroll', () => this.queueScroll(), { signal, passive: true });
      window.addEventListener('pageshow', () => {
        this.revealAndReset();
        this.queueMeasure();
      }, { signal });

      this.closeMegaMenus();
      this.close(this.mobileMenu);
      heightOwner = this;
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.queueMeasure());
        this.resizeObserver.observe(this);
      }
      window.addEventListener('resize', () => {
        this.revealAndReset();
        this.queueMeasure();
      }, { signal, passive: true });
      document.fonts?.ready.then(() => { if (!signal.aborted) this.queueMeasure(); });
      this.queueMeasure();
    }

    close(menu, restoreFocus = false) {
      if (!menu?.open) return;
      menu.open = false;
      if (restoreFocus) {
        this.revealAndReset();
        menu.querySelector(':scope > summary')?.focus({ preventScroll: true });
      }
    }

    closeMegaMenus(except = null) {
      this.megaMenus?.forEach(menu => { if (menu !== except) this.close(menu); });
    }

    ownsEditorEvent(event) {
      const target = event.target;
      if (target instanceof Node && (target === this.wrapper || this.contains(target))) return true;
      const sectionId = event.detail?.sectionId;
      return Boolean(sectionId && this.wrapper?.id === `shopify-section-${sectionId}`);
    }

    hasOpenMenu() {
      return this.mobileMenu?.open || this.megaMenus?.some(menu => menu.open);
    }

    shouldStayVisible() {
      const active = document.activeElement;
      const activeInHeader = this.contains(active);
      const editing = activeInHeader && active.matches('input,textarea,select,[contenteditable="true"]');
      const keyboardFocus = activeInHeader && (this.keyboardNavigation || active.matches(':focus-visible'));
      return this.hasOpenMenu() || editing || keyboardFocus
        || this.editorSectionSelected || this.editorBlockId;
    }

    scrollPosition() {
      const scrolling = document.scrollingElement || document.documentElement;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const maximum = Math.max(0, scrolling.scrollHeight - viewportHeight);
      // iOS elastic overscroll must not be interpreted as a change of direction.
      const current = typeof window.scrollY === 'number' ? window.scrollY : scrolling.scrollTop || 0;
      return Math.max(0, Math.min(maximum, current));
    }

    resetScrollTracking() {
      this.lastScrollY = this.scrollPosition();
      this.scrollDirection = 0;
      this.scrollDistance = 0;
    }

    revealAndReset() {
      this.setHidden(false);
      this.resetScrollTracking();
    }

    queueScroll() {
      if (!this.isConnected || heightOwner !== this || this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (!this.isConnected || heightOwner !== this) return;
        const position = this.scrollPosition();
        const delta = position - this.lastScrollY;
        this.lastScrollY = position;
        // Native dialogs lock the page. Preserve its prior header state during their scroll events.
        if (document.querySelector('dialog[open]')) {
          this.scrollDirection = 0;
          this.scrollDistance = 0;
          return;
        }
        const canHide = this.dataset.sticky === 'true' && this.dataset.stickyMode !== 'always';
        if (!canHide || position <= this.headerHeight || this.shouldStayVisible()) {
          this.setHidden(false);
          this.scrollDirection = 0;
          this.scrollDistance = 0;
          return;
        }
        if (delta === 0) return;
        const direction = Math.sign(delta);
        if (direction !== this.scrollDirection) this.scrollDistance = 0;
        this.scrollDirection = direction;
        this.scrollDistance += Math.abs(delta);
        // Twelve pixels of consistent movement avoid flicker from tiny scroll corrections.
        if (this.scrollDistance >= 12) {
          this.setHidden(direction > 0);
          this.scrollDistance = 0;
        }
      });
    }

    setHidden(hidden) {
      const next = Boolean(hidden && this.wrapper);
      this.headerHidden = next;
      this.wrapper?.classList.toggle('bc-header-is-hidden', next);
      this.syncMetrics();
    }

    syncMetrics() {
      if (!this.isConnected || heightOwner !== this) return;
      const height = this.headerHeight || 0;
      const sticky = this.dataset.sticky === 'true';
      const offset = sticky && !this.headerHidden ? height : 0;
      const style = document.documentElement.style;
      if (style.getPropertyValue('--bc-header-height') !== `${height}px`) style.setProperty('--bc-header-height', `${height}px`);
      if (style.getPropertyValue('--bc-header-offset') !== `${offset}px`) style.setProperty('--bc-header-offset', `${offset}px`);
      const metrics = `${height}:${offset}:${this.headerHidden}:${sticky}`;
      if (metrics === this.lastMetrics) return;
      this.lastMetrics = metrics;
      this.dispatchEvent(new CustomEvent('beerclub:header-visibility-change', {
        bubbles: true,
        detail: { visible: !this.headerHidden, height, offset, sticky }
      }));
    }

    queueMeasure() {
      if (!this.isConnected || heightOwner !== this || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.isConnected || heightOwner !== this) return;
        const height = Math.ceil(this.getBoundingClientRect().height);
        if (height !== this.headerHeight) {
          this.headerHeight = height;
          this.revealAndReset();
        }
        // Only write changed values, so ResizeObserver never feeds an identical size back.
        this.syncMetrics();
      });
    }

    attributeChangedCallback() {
      if (!this.controller) return;
      this.revealAndReset();
      this.queueMeasure();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.scrollFrame);
      this.frame = 0;
      this.scrollFrame = 0;
      this.closeMegaMenus();
      this.close(this.mobileMenu);
      this.closeButtons?.forEach(button => { button.hidden = true; });
      this.classList.remove('is-enhanced');
      this.wrapper?.classList.remove('bc-header-is-hidden');
      this.editorBlockId = null;
      this.editorSectionSelected = false;
      if (heightOwner !== this) return;
      // Theme-editor replacements can connect before the old section is removed.
      heightOwner = [...document.querySelectorAll('beerclub-header')].reverse()
        .find(header => header !== this && header.controller) || null;
      if (heightOwner) heightOwner.queueMeasure();
      else {
        document.documentElement.style.removeProperty('--bc-header-height');
        document.documentElement.style.removeProperty('--bc-header-offset');
      }
    }
  }

  customElements.define('beerclub-header', BeerclubHeader);
})();
